import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { ArrowLeft, Home, Ruler, Calculator, FileText, ClipboardList, Plus, Eye, Upload, Edit2, Trash2, Sparkles } from "lucide-react";
import { useTradeWorkflows, type WorkflowStage } from "@/hooks/useTradeWorkflows";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { triggerWorkflowAutomations } from "@/utils/workflowAutomations";
import { CreateProposalDialog } from "@/components/proposals/CreateProposalDialog";
import { MeasurementImportDialog } from "@/components/measurements/MeasurementImportDialog";
import { ManualMeasurementDialog } from "@/components/measurements/ManualMeasurementDialog";
import { ManualRoofMeasurementDialog } from "@/components/measurements/ManualRoofMeasurementDialog";
import { ManualGutterMeasurementDialog } from "@/components/measurements/ManualGutterMeasurementDialog";
import { ManualSidingMeasurementDialog } from "@/components/measurements/ManualSidingMeasurementDialog";
import { MeasurementOptionsDialog } from "@/components/measurements/MeasurementOptionsDialog";
import { PackagePicker } from "@/components/job-detail/PackagePicker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface LineItem {
  id: string;
  item_name: string;
  description?: string;
  quantity: number;
  unit_type: string;
  material_cost: number;
  labor_cost: number;
  total_cost: number;
}

interface TradeDetailViewProps {
  jobId: string;
  orgId: string;
  tradeId: string;
  jobTrade: any;
  propertyType: string;
  jobData?: any;
  jobAddress?: string;
  onBack: () => void;
  onRefresh: () => void;
}

export const TradeDetailView = ({
  jobId,
  orgId,
  tradeId,
  jobTrade,
  propertyType,
  jobData,
  jobAddress,
  onBack,
  onRefresh,
}: TradeDetailViewProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("overview");
  const [showCreateProposal, setShowCreateProposal] = useState(false);
  const [showMeasurementImport, setShowMeasurementImport] = useState(false);
  const [showManualMeasurement, setShowManualMeasurement] = useState(false);
  const [showManualRoofDialog, setShowManualRoofDialog] = useState(false);
  const [showManualGutterDialog, setShowManualGutterDialog] = useState(false);
  const [showManualSidingDialog, setShowManualSidingDialog] = useState(false);
  const [showOptionsDialog, setShowOptionsDialog] = useState(false);
  const [generatingEstimate, setGeneratingEstimate] = useState(false);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [editingLineItem, setEditingLineItem] = useState<LineItem | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCatalogDialog, setShowCatalogDialog] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [generating, setGenerating] = useState(false);
  const [wastePercent, setWastePercent] = useState(0);
  const [overheadPercent, setOverheadPercent] = useState(10);
  const [profitPercent, setProfitPercent] = useState(15);
  const [currentStageKey, setCurrentStageKey] = useState<string | null>(jobTrade?.current_stage ?? null);
  const [localStatus, setLocalStatus] = useState<string | undefined>(jobTrade?.status);
  const { stages, loading: loadingWorkflow } = useTradeWorkflows(propertyType, jobTrade?.trade_code || "");

  // Keep local stage/status in sync when jobTrade changes
  useEffect(() => {
    if (jobTrade?.current_stage) {
      setCurrentStageKey(jobTrade.current_stage);
    }
    if (jobTrade?.status) {
      setLocalStatus(jobTrade.status);
    }
  }, [jobTrade?.current_stage, jobTrade?.status]);

  // Helper function to get measurement config based on trade code
  const getMeasurementConfig = (code: string) => {
    const normalized = code?.toLowerCase() || '';
    if (normalized.includes('roof')) {
      return { table: 'measurements_roof', allowImport: true, allowAI: true, allowManual: true, allowSolar: true };
    }
    if (normalized.includes('gutter')) {
      return { table: 'measurements_gutters', allowImport: true, allowAI: false, allowManual: true };
    }
    if (normalized.includes('siding')) {
      return { table: 'measurements_siding', allowImport: true, allowAI: false, allowManual: true };
    }
    // Generic trades: decking, windows, painting, etc.
    return { table: 'measurements_generic', allowImport: false, allowAI: false, allowManual: true };
  };

  const measurementConfig = getMeasurementConfig(jobTrade?.trade_code || '');
  const measurementTable = measurementConfig.table;

  // Fetch trade-specific measurements
  const { data: measurements, isLoading: loadingMeasurements, refetch: refetchMeasurements } = useQuery({
    queryKey: ["trade-measurements", jobId, tradeId, measurementTable],
    queryFn: async () => {
      // Only query if this trade type has measurements
      if (measurementTable === 'measurements_generic') {
        const { data, error } = await supabase
          .from('measurements_generic')
          .select('*')
          .eq('job_id', jobId)
          .eq('job_trade_id', tradeId)
          .order('created_at', { ascending: false });
        if (error) throw error;
        return data || [];
      } else {
        const { data, error } = await supabase
          .from(measurementTable as any)
          .select('*')
          .eq('job_id', jobId)
          .eq('job_trade_id', tradeId)
          .order('created_at', { ascending: false });
        if (error) throw error;
        return data || [];
      }
    },
    enabled: !!jobId && !!tradeId && !!measurementTable,
  });

  // Fetch trade-specific estimates
  const { data: estimates, isLoading: loadingEstimates, refetch: refetchEstimates } = useQuery({
    queryKey: ["trade-estimates", jobId, tradeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("job_estimates")
        .select("*")
        .eq("job_id", jobId)
        .eq("trade_id", tradeId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  // Fetch proposals for this job
  const { data: proposals, isLoading: loadingProposals, refetch: refetchProposals } = useQuery({
    queryKey: ["trade-proposals", jobId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("proposal_documents")
        .select("*")
        .eq("job_id", jobId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  // Fetch form submissions for this job
  const { data: formSubmissions, isLoading: loadingForms, refetch: refetchForms } = useQuery({
    queryKey: ["trade-forms", jobId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("form_submissions")
        .select(`
          *,
          form_template:form_templates(*)
        `)
        .eq("job_id", jobId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  // Fetch catalog items for adding line items
  const { data: catalogItems } = useQuery({
    queryKey: ["catalog-items", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("catalog_items")
        .select("*")
        .eq("org_id", orgId)
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      return data || [];
    },
  });

  // Get measurement value and label based on trade type
  const latestMeasurement = (measurements?.[0] as any) || null;

  const formatMeasurementValue = (value: number) => {
    if (!Number.isFinite(value) || value <= 0) return "—";
    return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(1);
  };

  const getMeasurementDisplay = () => {
    const tradeCode = jobTrade?.trade_code?.toLowerCase() || '';
    
    if (tradeCode.includes('roof')) {
      const total = latestMeasurement?.total_squares || 0;
      const area = latestMeasurement?.total_area || 0;
      const pitch = latestMeasurement?.pitch || latestMeasurement?.raw_response?.parsed_data?.predominant_pitch;
      const facets = latestMeasurement?.raw_response?.parsed_data?.number_of_facets;
      return {
        value: formatMeasurementValue(total),
        label: area > 0 ? `${formatMeasurementValue(area)} sq ft` : 'Roofing report',
        meta: [pitch ? `Pitch ${pitch}` : null, facets ? `${facets} facets` : null].filter(Boolean).join(' • '),
      };
    } else if (tradeCode.includes('gutter')) {
      const total = latestMeasurement?.total_gutter_length || 0;
      return { value: formatMeasurementValue(total), label: 'Linear Ft', meta: '' };
    } else if (tradeCode.includes('siding')) {
      const total = latestMeasurement?.total_area || 0;
      return { value: formatMeasurementValue(total), label: 'Total Sq Ft', meta: '' };
    } else {
      const total = latestMeasurement?.total_area || 0;
      return { value: formatMeasurementValue(total), label: 'Total Sq Ft', meta: '' };
    }
  };

  // Load line items for the currently selected package (default: better)
  const loadLineItems = async (estimateId: string, packageFilter?: string | null) => {
    let query = supabase
      .from("estimate_line_items")
      .select("*")
      .eq("estimate_id", estimateId)
      .order("item_name");

    const pkg = packageFilter || (latestEstimate as any)?.selected_package || "better";
    // Only filter when at least one row has a package set; otherwise show all (legacy estimates)
    const { data: probe } = await supabase
      .from("estimate_line_items")
      .select("id")
      .eq("estimate_id", estimateId)
      .not("package", "is", null)
      .limit(1);
    if (probe && probe.length > 0) {
      query = query.eq("package", pkg);
    }

    const { data, error } = await query;
    if (error) {
      console.error("Error loading line items:", error);
      toast.error("Failed to load line items");
      return;
    }
    setLineItems(data || []);
    await updateTradeSubtotal(data || []);
  };

  const handleSelectPackage = async (tier: "good" | "better" | "best") => {
    if (!latestEstimate?.id) return;
    const prices = (latestEstimate as any)?.package_prices || {};
    const subtotal = Number(prices[tier] || 0);
    const overheadAmt = subtotal * (overheadPercent / 100);
    const profitAmt = subtotal * (profitPercent / 100);
    await supabase.from("job_estimates").update({
      selected_package: tier,
      cost_subtotal: subtotal,
      overhead_amount: overheadAmt,
      profit_amount: profitAmt,
      customer_price: subtotal + overheadAmt + profitAmt,
    }).eq("id", latestEstimate.id);
    await loadLineItems(latestEstimate.id, tier);
    refetchEstimates();
    toast.success(`Switched to ${tier.charAt(0).toUpperCase() + tier.slice(1)} package`);
  };

  // Update job_trades subtotal whenever line items change
  const updateTradeSubtotal = async (items: LineItem[]) => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * (item.material_cost + item.labor_cost)), 0);
    const wasteAmount = subtotal * (wastePercent / 100);
    const subtotalWithWaste = subtotal + wasteAmount;
    const overheadAmount = subtotalWithWaste * (overheadPercent / 100);
    const profitAmount = subtotalWithWaste * (profitPercent / 100);
    const grandTotal = subtotalWithWaste + overheadAmount + profitAmount;

    try {
      await supabase
        .from("job_trades")
        .update({ subtotal: grandTotal })
        .eq("id", tradeId);
    } catch (error) {
      console.error("Error updating trade subtotal:", error);
    }
  };

  const measurementDisplay = getMeasurementDisplay();
  const latestEstimate = estimates?.[0];

  // Load line items when first estimate is available
  useEffect(() => {
    if (latestEstimate?.id) {
      loadLineItems(latestEstimate.id);
    }
  }, [latestEstimate?.id]);

  const handleGenerateEstimate = async () => {
    if (!measurements || measurements.length === 0) {
      toast.error("No measurements found for this trade");
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Please log in to generate estimates");
        return;
      }

      toast.info("Generating estimate...");

      const measurementId = (measurements as any)?.[0]?.id;
      
      if (!measurementId) {
        toast.error("No valid measurement found. Please add measurements first.");
        return;
      }

      const { data, error } = await supabase.functions.invoke("bridgette-generate-estimate", {
        body: {
          measurement_id: measurementId,
          job_id: jobId,
          trade_id: tradeId,
          trade_code: jobTrade?.trade_code,
        },
      });

      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }

      if (data?.success) {
        toast.success("Estimate generated successfully!");
        setActiveTab("estimate"); // Switch to estimate tab
        refetchEstimates();
        onRefresh();
      } else {
        throw new Error(data?.error || "Failed to generate estimate");
      }
    } catch (error: any) {
      console.error("Error generating estimate:", error);
      toast.error(error.message || "Failed to generate estimate");
    }
  };

  const handleTakeMeasurements = () => {
    const measurementConfig = getMeasurementConfig(jobTrade.trade_code);
    const normalized = jobTrade.trade_code.toLowerCase();
    
    // For roofing, show options dialog
    if (normalized.includes('roof') && measurementConfig.allowSolar) {
      setShowOptionsDialog(true);
    } else if (normalized.includes('gutter')) {
      setShowManualGutterDialog(true);
    } else if (normalized.includes('siding')) {
      setShowManualSidingDialog(true);
    } else if (measurementConfig.allowManual) {
      setShowManualMeasurement(true);
    } else if (measurementConfig.allowImport) {
      setShowMeasurementImport(true);
    }
  };

  const handleViewMeasurement = async (m: any) => {
    const raw = m?.raw_response || {};
    const bucket = raw.storage_bucket || "job-documents";
    const path = raw.storage_path;
    if (path) {
      try {
        const { data, error } = await (supabase.storage as any)
          .from(bucket)
          .createSignedUrl(path, 60 * 10);
        if (error) throw error;
        const url = (data as any)?.signedUrl ?? (data as any)?.signedURL;
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer");
          return;
        }
      } catch (err: any) {
        console.error("Open measurement PDF failed", err);
        toast.error("Could not open the original PDF");
      }
    } else {
      toast.info("No source PDF saved for this measurement. Re-upload to enable preview.");
    }
  };

  const handleGoogleSolar = async () => {
    if (!jobAddress) {
      toast.error("Job address is required for Google Solar measurements");
      return;
    }

    setGeneratingEstimate(true);
    try {
      const { data, error } = await supabase.functions.invoke('google-solar-measurement', {
        body: {
          address: jobAddress,
          job_id: jobId,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success("Google Solar measurements retrieved successfully");
      refetchMeasurements();
      setActiveTab('measurements');
    } catch (error: any) {
      console.error('Error getting Google Solar measurements:', error);
      toast.error(error.message || "Failed to retrieve Google Solar measurements");
    } finally {
      setGeneratingEstimate(false);
    }
  };

  const handleMeasurementSuccess = () => {
    refetchMeasurements();
    setActiveTab('measurements');
    toast.success("Measurements saved successfully!");
  };

  const handleCloseProposalDialog = () => {
    setShowCreateProposal(false);
    refetchProposals();
    onRefresh();
  };

  const handleAISuggest = async () => {
    if (!latestEstimate?.id) {
      toast.error("No estimate found");
      return;
    }

    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("suggest-estimate-items", {
        body: {
          estimate_id: latestEstimate.id,
          trade_code: jobTrade?.trade_code,
        },
      });

      if (error) throw error;

      if (data?.success) {
        toast.success("Line items regenerated!");
        await loadLineItems(latestEstimate.id);
      }
    } catch (error: any) {
      console.error("Error regenerating line items:", error);
      toast.error(error.message || "Failed to regenerate line items");
    } finally {
      setGenerating(false);
    }
  };

  const handleAddLineItem = async (catalogItem: any) => {
    if (!latestEstimate?.id) return;

    const hasCosts = catalogItem.material_cost !== null || catalogItem.labor_cost !== null;
    
    try {
      const newItem = {
        estimate_id: latestEstimate.id,
        item_name: catalogItem.name,
        description: catalogItem.description,
        quantity: 1,
        unit_type: catalogItem.unit_type || "each",
        material_cost: catalogItem.material_cost || 0,
        labor_cost: catalogItem.labor_cost || 0,
        total_cost: (catalogItem.material_cost || 0) + (catalogItem.labor_cost || 0),
      };

      const { data, error } = await supabase
        .from("estimate_line_items")
        .insert(newItem)
        .select()
        .single();

      if (error) throw error;

      toast.success("Line item added!");
      await loadLineItems(latestEstimate.id);
      setShowCatalogDialog(false);

      // If no costs, open edit dialog immediately
      if (!hasCosts) {
        setEditingLineItem(data as LineItem);
        setShowEditDialog(true);
        toast.info("Please set material and labor costs");
      }
    } catch (error: any) {
      console.error("Error adding line item:", error);
      toast.error("Failed to add line item");
    }
  };

  const handleUpdateLineItem = async (updates: Partial<LineItem>) => {
    if (!editingLineItem) return;

    try {
      const quantity = updates.quantity !== undefined ? updates.quantity : editingLineItem.quantity;
      const materialCost = updates.material_cost !== undefined ? updates.material_cost : editingLineItem.material_cost;
      const laborCost = updates.labor_cost !== undefined ? updates.labor_cost : editingLineItem.labor_cost;

      const updatedItem = {
        ...updates,
        total_cost: quantity * (materialCost + laborCost),
      };

      const { error } = await supabase
        .from("estimate_line_items")
        .update(updatedItem)
        .eq("id", editingLineItem.id);

      if (error) throw error;

      toast.success("Line item updated!");
      await loadLineItems(latestEstimate!.id);
      setShowEditDialog(false);
      setEditingLineItem(null);
    } catch (error: any) {
      console.error("Error updating line item:", error);
      toast.error("Failed to update line item");
    }
  };

  const handleDeleteLineItem = async (id: string) => {
    try {
      const { error } = await supabase
        .from("estimate_line_items")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success("Line item deleted!");
      await loadLineItems(latestEstimate!.id);
    } catch (error: any) {
      console.error("Error deleting line item:", error);
      toast.error("Failed to delete line item");
    }
  };

  const handleStageClick = async (stage: WorkflowStage) => {
    try {
      const previousStageKey = currentStageKey || jobTrade?.current_stage || "";
      const updates: { current_stage: string; status?: string | null } = {
        current_stage: stage.stage_key,
      };

      if (stage.job_status) {
        updates.status = stage.job_status;
      }

      const { error } = await supabase
        .from("job_trades")
        .update(updates)
       .eq("id", tradeId);

      if (error) throw error;

      // Update local UI immediately
      setCurrentStageKey(stage.stage_key);
      if (stage.job_status) {
        setLocalStatus(stage.job_status);
      }

      // Refresh job trades list so overview cards update without page reload
      queryClient.invalidateQueries({ queryKey: ["job-trades", jobId] });

      // Fire workflow automations for the new stage (background, non-blocking)
      if (previousStageKey !== stage.stage_key) {
        const wfType = (["retail", "insurance", "commercial"].includes(propertyType)
          ? propertyType
          : "retail") as "retail" | "insurance" | "commercial";
        triggerWorkflowAutomations({
          jobId,
          oldStatus: previousStageKey,
          newStatus: stage.stage_key,
          workflowType: wfType,
        }).catch((err) => console.error("Automation fire failed:", err));
      }

      toast.success("Stage updated!");
    } catch (error: any) {
      console.error("Error updating stage:", error);
      toast.error("Failed to update stage");
    }
  };

  const currentStageIndexRaw = stages.findIndex(s => s.stage_key === (currentStageKey || jobTrade?.current_stage));
  const fallbackStageIndex = (localStatus || jobTrade?.status)
    ? stages.findIndex(s => s.job_status === (localStatus || jobTrade?.status))
    : -1;
  const currentStageIndex = currentStageIndexRaw >= 0
    ? currentStageIndexRaw
    : (fallbackStageIndex >= 0 ? fallbackStageIndex : 0);
  const progress = stages.length > 0 ? ((currentStageIndex + 1) / stages.length) * 100 : 0;
  const currentStage = stages[currentStageIndex];

  const tradeIcon = {
    roofing: "🏠",
    gutters: "💧",
    siding: "🏗️",
    decking: "🪵",
    windows: "🪟",
  }[jobTrade?.trade_code?.toLowerCase()] || "🔧";


  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Trades
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-2xl">
              {tradeIcon}
            </div>
            <div>
              <h2 className="text-2xl font-display font-bold">{jobTrade?.trades?.trade_name || "Trade"}</h2>
              <p className="text-sm text-muted-foreground">
                Status: <span className="font-medium text-foreground">{localStatus || jobTrade?.status || "Active"}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Workflow Progress */}
      {!loadingWorkflow && stages.length > 0 && (
        <Card className="p-6 bg-gradient-to-br from-primary/5 to-secondary/5">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Workflow Progress</h3>
                <p className="text-lg font-bold mt-1">{currentStage?.stage_name || "In Progress"}</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-display font-bold text-primary">{Math.round(progress)}%</div>
                <div className="text-xs text-muted-foreground">Complete</div>
              </div>
            </div>
            <Progress value={progress} className="h-2" />
            <div className="flex flex-wrap gap-2 pt-2">
              {stages.map((stage, idx) => (
                <button
                  key={stage.id}
                  type="button"
                  onClick={() => handleStageClick(stage)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer hover:scale-105 ${
                    idx <= currentStageIndex
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  {stage.stage_name}
                </button>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6 bg-gradient-to-br from-success/10 to-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-muted-foreground uppercase">Revenue</div>
            <Calculator className="w-5 h-5 text-success" />
          </div>
          <div className="text-3xl font-display font-bold text-success">
            {(() => {
              if (lineItems.length === 0) {
                return jobTrade?.subtotal ? `$${jobTrade.subtotal.toLocaleString()}` : "—";
              }
              const subtotal = lineItems.reduce(
                (sum, item) => sum + (item.quantity * (item.material_cost + item.labor_cost)),
                0
              );
              const wasteAmount = subtotal * (wastePercent / 100);
              const subtotalWithWaste = subtotal + wasteAmount;
              const overheadAmount = subtotalWithWaste * (overheadPercent / 100);
              const profitAmount = subtotalWithWaste * (profitPercent / 100);
              const grandTotal = subtotalWithWaste + overheadAmount + profitAmount;
              return `$${grandTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
            })()}
          </div>
          <div className="text-xs text-muted-foreground mt-1">Trade Subtotal</div>
        </Card>

        <Card className="p-6 bg-card border-border/70">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-muted-foreground uppercase">Measurements</div>
            <Ruler className="w-5 h-5 text-primary" />
          </div>
          <div className="text-3xl font-display font-bold text-foreground">
            {measurementDisplay.value}
          </div>
          <div className="text-xs text-muted-foreground mt-1">{measurementDisplay.label}</div>
          {measurementDisplay.meta ? (
            <div className="text-xs text-muted-foreground mt-1">{measurementDisplay.meta}</div>
          ) : null}
        </Card>

        <Card className="p-6 bg-gradient-to-br from-primary/10 to-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-muted-foreground uppercase">Estimates</div>
            <FileText className="w-5 h-5 text-primary" />
          </div>
          <div className="text-3xl font-display font-bold text-foreground">
            {estimates?.length || 0}
          </div>
          <div className="text-xs text-muted-foreground mt-1">Generated</div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">
            <Home className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="measurements">
            <Ruler className="w-4 h-4 mr-2" />
            Measurements
          </TabsTrigger>
          <TabsTrigger value="estimate">
            <Calculator className="w-4 h-4 mr-2" />
            Estimate
          </TabsTrigger>
          <TabsTrigger value="proposal">
            <FileText className="w-4 h-4 mr-2" />
            Proposal
          </TabsTrigger>
          <TabsTrigger value="forms">
            <ClipboardList className="w-4 h-4 mr-2" />
            Forms
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button variant="outline" size="lg" className="w-full justify-start" onClick={handleTakeMeasurements}>
                {measurementConfig.allowManual ? (
                  <>
                    <Ruler className="w-5 h-5 mr-3" />
                    <div className="text-left">
                      <div className="font-semibold">Enter Measurements</div>
                      <div className="text-xs text-muted-foreground">Manually input measurements</div>
                    </div>
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-3" />
                    <div className="text-left">
                      <div className="font-semibold">Import Measurements</div>
                      <div className="text-xs text-muted-foreground">Upload Hover, GAF, or Roofr reports</div>
                    </div>
                  </>
                )}
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="w-full justify-start"
                onClick={handleGenerateEstimate}
                disabled={!measurements || measurements.length === 0}
              >
                <Calculator className="w-5 h-5 mr-3" />
                <div className="text-left">
                  <div className="font-semibold">Generate Estimate</div>
                  <div className="text-xs text-muted-foreground">AI-powered pricing estimate</div>
                </div>
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="w-full justify-start"
                onClick={() => setShowCreateProposal(true)}
                disabled={!estimates || estimates.length === 0}
              >
                <FileText className="w-5 h-5 mr-3" />
                <div className="text-left">
                  <div className="font-semibold">Create Proposal</div>
                  <div className="text-xs text-muted-foreground">
                    {(!estimates || estimates.length === 0) 
                      ? "Generate an estimate first" 
                      : "Build customer proposal"}
                  </div>
                </div>
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="w-full justify-start"
                onClick={() => setActiveTab("forms")}
              >
                <ClipboardList className="w-5 h-5 mr-3" />
                <div className="text-left">
                  <div className="font-semibold">Fill Forms</div>
                  <div className="text-xs text-muted-foreground">Complete required forms</div>
                </div>
              </Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="measurements" className="space-y-4 mt-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Measurements</h3>
              <Button size="sm" onClick={handleTakeMeasurements}>
                {measurementConfig.allowManual ? (
                  <>
                    <Ruler className="w-4 h-4 mr-2" />
                    Enter Measurements
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import Measurements
                  </>
                )}
              </Button>
            </div>
            {loadingMeasurements ? (
              <div className="text-center py-8 text-muted-foreground">Loading measurements...</div>
            ) : measurements && measurements.length > 0 ? (
              <div className="space-y-3">
                {measurements.map((m: any) => (
                  <div key={m.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold">
                          {m.total_squares ? `${Number(m.total_squares).toFixed(1)} SQ` : 
                           m.total_gutter_length ? `${Number(m.total_gutter_length).toFixed(1)} LF gutter` :
                           m.total_area ? `${Number(m.total_area).toFixed(0)} sq ft` : "Measurement report"}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {new Date(m.created_at).toLocaleDateString()}
                        </div>
                        {m.total_squares ? (
                          <div className="text-xs text-muted-foreground mt-1">
                            {[
                              m.pitch ? `Pitch ${m.pitch}` : null,
                              m.ridges ? `Ridges ${Number(m.ridges).toFixed(1)} LF` : null,
                              m.hips ? `Hips ${Number(m.hips).toFixed(1)} LF` : null,
                              m.valleys ? `Valleys ${Number(m.valleys).toFixed(1)} LF` : null,
                              m.eaves ? `Eaves ${Number(m.eaves).toFixed(1)} LF` : null,
                              m.rakes ? `Rakes ${Number(m.rakes).toFixed(1)} LF` : null,
                            ].filter(Boolean).join(' • ')}
                          </div>
                        ) : null}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleViewMeasurement(m)}>
                        <Eye className="w-4 h-4 mr-2" />
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Ruler className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No measurements yet for this trade</p>
                <Button className="mt-4" onClick={handleTakeMeasurements}>
                  {measurementConfig.allowManual ? (
                    <>
                      <Ruler className="w-4 h-4 mr-2" />
                      Enter Measurements
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import Measurements
                    </>
                  )}
                </Button>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="estimate" className="space-y-4 mt-6">
          {!latestEstimate ? (
            <Card className="p-6">
              <div className="text-center py-12">
                <Calculator className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No estimates yet for this trade</p>
                <Button className="mt-4" onClick={handleGenerateEstimate} disabled={!measurements || measurements.length === 0}>
                  <Calculator className="w-4 h-4 mr-2" />
                  Generate First Estimate
                </Button>
              </div>
            </Card>
          ) : (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold">Line Items</h3>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleAISuggest}
                    disabled={generating}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {generating ? "Generating..." : "AI Suggest"}
                  </Button>
                  <Button size="sm" onClick={() => setShowCatalogDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                </div>
              </div>

              <PackagePicker
                packagePrices={(latestEstimate as any)?.package_prices}
                selected={(latestEstimate as any)?.selected_package || "better"}
                onSelect={handleSelectPackage}
              />

              {lineItems.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No line items yet. Click "AI Suggest" to generate items or "Add Item" to add manually.
                </div>
              ) : (
                <>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Item</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead>Unit</TableHead>
                          <TableHead className="text-right">Material</TableHead>
                          <TableHead className="text-right">Labor</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="w-24"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {lineItems.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <div className="font-medium">{item.item_name}</div>
                              {item.description && (
                                <div className="text-xs text-muted-foreground">{item.description}</div>
                              )}
                            </TableCell>
                            <TableCell className="text-right">{item.quantity}</TableCell>
                            <TableCell>{item.unit_type}</TableCell>
                            <TableCell className="text-right">${item.material_cost.toFixed(2)}</TableCell>
                            <TableCell className="text-right">${item.labor_cost.toFixed(2)}</TableCell>
                            <TableCell className="text-right font-semibold">${item.total_cost.toFixed(2)}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditingLineItem(item);
                                    setShowEditDialog(true);
                                  }}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteLineItem(item.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Adjustments Section */}
                  <div className="mt-6 space-y-6 border-t pt-6">
                    <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Adjustments</h4>
                    
                    {/* Waste Slider */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Label className="text-sm font-medium">Waste Factor</Label>
                        <span className="text-sm font-semibold text-primary">{wastePercent}%</span>
                      </div>
                      <Slider
                        value={[wastePercent]}
                        onValueChange={(value) => setWastePercent(value[0])}
                        max={30}
                        step={1}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>0%</span>
                        <span>30%</span>
                      </div>
                    </div>

                    {/* Overhead Slider */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Label className="text-sm font-medium">Overhead</Label>
                        <span className="text-sm font-semibold text-primary">{overheadPercent}%</span>
                      </div>
                      <Slider
                        value={[overheadPercent]}
                        onValueChange={(value) => setOverheadPercent(value[0])}
                        max={25}
                        step={1}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>0%</span>
                        <span>25%</span>
                      </div>
                    </div>

                    {/* Profit Slider */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Label className="text-sm font-medium">Profit Margin</Label>
                        <span className="text-sm font-semibold text-primary">{profitPercent}%</span>
                      </div>
                      <Slider
                        value={[profitPercent]}
                        onValueChange={(value) => setProfitPercent(value[0])}
                        max={50}
                        step={1}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>0%</span>
                        <span>50%</span>
                      </div>
                    </div>
                  </div>

                  {/* Summary Footer */}
                  <div className="mt-6 space-y-2 border-t pt-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Materials Total:</span>
                      <span className="font-medium">
                        ${lineItems.reduce((sum, item) => sum + (item.quantity * item.material_cost), 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Labor Total:</span>
                      <span className="font-medium">
                        ${lineItems.reduce((sum, item) => sum + (item.quantity * item.labor_cost), 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal (Line Items):</span>
                      <span className="font-medium">
                        ${lineItems.reduce((sum, item) => sum + (item.quantity * (item.material_cost + item.labor_cost)), 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">+ Waste ({wastePercent}%):</span>
                      <span className="font-medium">
                        ${(lineItems.reduce((sum, item) => sum + (item.quantity * (item.material_cost + item.labor_cost)), 0) * (wastePercent / 100)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm font-semibold border-t pt-2">
                      <span>Materials + Labor + Waste:</span>
                      <span>
                        ${(lineItems.reduce((sum, item) => sum + (item.quantity * (item.material_cost + item.labor_cost)), 0) * (1 + wastePercent / 100)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">+ Overhead ({overheadPercent}%):</span>
                      <span className="font-medium">
                        ${(lineItems.reduce((sum, item) => sum + (item.quantity * (item.material_cost + item.labor_cost)), 0) * (1 + wastePercent / 100) * (overheadPercent / 100)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">+ Profit ({profitPercent}%):</span>
                      <span className="font-medium">
                        ${(lineItems.reduce((sum, item) => sum + (item.quantity * (item.material_cost + item.labor_cost)), 0) * (1 + wastePercent / 100) * (profitPercent / 100)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                      <span>Grand Total:</span>
                      <span className="text-success">
                        ${(() => {
                          const subtotal = lineItems.reduce((sum, item) => sum + (item.quantity * (item.material_cost + item.labor_cost)), 0);
                          const wasteAmount = subtotal * (wastePercent / 100);
                          const subtotalWithWaste = subtotal + wasteAmount;
                          const overheadAmount = subtotalWithWaste * (overheadPercent / 100);
                          const profitAmount = subtotalWithWaste * (profitPercent / 100);
                          return (subtotalWithWaste + overheadAmount + profitAmount).toFixed(2);
                        })()}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </Card>
          )}
        </TabsContent>

        <TabsContent value="proposal" className="space-y-4 mt-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Proposals</h3>
              <Button 
                size="sm" 
                onClick={() => setShowCreateProposal(true)}
                disabled={!estimates || estimates.length === 0}
              >
                <Plus className="w-4 h-4 mr-2" />
                New Proposal
              </Button>
            </div>
            {loadingProposals ? (
              <div className="text-center py-8 text-muted-foreground">Loading proposals...</div>
            ) : proposals && proposals.length > 0 ? (
              <div className="space-y-3">
                {proposals.map((proposal: any) => (
                  <div key={proposal.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold">{proposal.title || "Untitled Proposal"}</div>
                        <div className="text-sm text-muted-foreground">
                          {new Date(proposal.created_at).toLocaleDateString()} • {proposal.status}
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate(`/proposals/review/${proposal.id}`)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">
                  {!estimates || estimates.length === 0 
                    ? "Generate an estimate first to create a proposal"
                    : "No proposals yet for this trade"}
                </p>
                <Button 
                  className="mt-4" 
                  onClick={() => setShowCreateProposal(true)}
                  disabled={!estimates || estimates.length === 0}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Proposal
                </Button>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="forms" className="space-y-4 mt-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Forms</h3>
              <Button size="sm" onClick={() => navigate(`/jobs/${jobId}`, { state: { openForms: true } })}>
                <Plus className="w-4 h-4 mr-2" />
                Add Form
              </Button>
            </div>
            {loadingForms ? (
              <div className="text-center py-8 text-muted-foreground">Loading forms...</div>
            ) : formSubmissions && formSubmissions.length > 0 ? (
              <div className="space-y-3">
                {formSubmissions.map((submission: any) => (
                  <div key={submission.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold">
                          {submission.form_template?.form_name || "Form"}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {new Date(submission.created_at).toLocaleDateString()} • {submission.status}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4 mr-2" />
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <ClipboardList className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No forms submitted yet for this job</p>
                <Button className="mt-4" onClick={() => navigate(`/jobs/${jobId}`, { state: { openForms: true } })}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Form
                </Button>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <CreateProposalDialog
        open={showCreateProposal}
        onOpenChange={handleCloseProposalDialog}
        jobId={jobId}
        jobData={{
          ...jobData,
          trade_type: jobTrade?.trade_code,
          trade_name: jobTrade?.trade_name,
        }}
        estimateId={latestEstimate?.id}
      />

      {/* Measurement Import Dialog */}
      {measurementConfig.allowImport && (
        <MeasurementImportDialog
          open={showMeasurementImport}
          onOpenChange={setShowMeasurementImport}
          jobId={jobId}
          tradeId={tradeId}
          tradeCode={jobTrade?.trade_code || ''}
          orgId={orgId}
          onSuccess={handleMeasurementSuccess}
        />
      )}

      {/* Manual Measurement Dialog */}
      {measurementConfig.allowManual && (
        <ManualMeasurementDialog
          open={showManualMeasurement}
          onOpenChange={setShowManualMeasurement}
          jobId={jobId}
          tradeId={tradeId}
          tradeCode={jobTrade?.trade_code || ''}
          tradeName={jobTrade?.trades?.trade_name || ''}
          orgId={orgId}
          onSuccess={handleMeasurementSuccess}
        />
      )}

      {/* Edit Line Item Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Line Item</DialogTitle>
          </DialogHeader>
          {editingLineItem && (
            <div className="space-y-4">
              <div>
                <Label>Item Name</Label>
                <Input
                  value={editingLineItem.item_name}
                  onChange={(e) => setEditingLineItem({ ...editingLineItem, item_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={editingLineItem.description || ""}
                  onChange={(e) => setEditingLineItem({ ...editingLineItem, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    value={editingLineItem.quantity}
                    onChange={(e) => setEditingLineItem({ ...editingLineItem, quantity: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>Unit Type</Label>
                  <Select
                    value={editingLineItem.unit_type}
                    onValueChange={(value) => setEditingLineItem({ ...editingLineItem, unit_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sq ft">sq ft</SelectItem>
                      <SelectItem value="linear ft">linear ft</SelectItem>
                      <SelectItem value="each">each</SelectItem>
                      <SelectItem value="square">square</SelectItem>
                      <SelectItem value="bundle">bundle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Material Cost</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingLineItem.material_cost}
                    onChange={(e) => setEditingLineItem({ ...editingLineItem, material_cost: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>Labor Cost</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingLineItem.labor_cost}
                    onChange={(e) => setEditingLineItem({ ...editingLineItem, labor_cost: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="bg-muted p-3 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Total Cost:</span>
                  <span className="text-lg font-bold text-success">
                    ${(editingLineItem.quantity * (editingLineItem.material_cost + editingLineItem.labor_cost)).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={() => handleUpdateLineItem(editingLineItem!)}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add from Catalog Dialog */}
      <Dialog open={showCatalogDialog} onOpenChange={setShowCatalogDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add from Catalog</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Search catalog items..."
              value={catalogSearch}
              onChange={(e) => setCatalogSearch(e.target.value)}
            />
            <div className="max-h-96 overflow-y-auto space-y-2">
              {catalogItems
                ?.filter((item) =>
                  item.name.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                  item.description?.toLowerCase().includes(catalogSearch.toLowerCase())
                )
                .map((item) => (
                  <div
                    key={item.id}
                    className="p-3 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                    onClick={() => handleAddLineItem(item)}
                  >
                    <div className="font-medium">{item.name}</div>
                    {item.description && (
                      <div className="text-sm text-muted-foreground">{item.description}</div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      Material: ${item.material_cost?.toFixed(2) || "0.00"} • Labor: ${item.labor_cost?.toFixed(2) || "0.00"}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Measurement Options Dialog (for roofing) */}
      <MeasurementOptionsDialog
        open={showOptionsDialog}
        onOpenChange={setShowOptionsDialog}
        onManualEntry={() => setShowManualRoofDialog(true)}
        onImportPDF={() => setShowMeasurementImport(true)}
        onGoogleSolar={handleGoogleSolar}
        showSolarOption={true}
      />

      {/* Manual Roof Measurement Dialog */}
      <ManualRoofMeasurementDialog
        open={showManualRoofDialog}
        onOpenChange={setShowManualRoofDialog}
        jobId={jobId}
        tradeId={tradeId}
        orgId={orgId}
        address={jobAddress}
        onSuccess={handleMeasurementSuccess}
      />

      {/* Manual Gutter Measurement Dialog */}
      <ManualGutterMeasurementDialog
        open={showManualGutterDialog}
        onOpenChange={setShowManualGutterDialog}
        jobId={jobId}
        tradeId={tradeId}
        orgId={orgId}
        address={jobAddress}
        onSuccess={handleMeasurementSuccess}
      />

      {/* Manual Siding Measurement Dialog */}
      <ManualSidingMeasurementDialog
        open={showManualSidingDialog}
        onOpenChange={setShowManualSidingDialog}
        jobId={jobId}
        tradeId={tradeId}
        orgId={orgId}
        address={jobAddress}
        onSuccess={handleMeasurementSuccess}
      />
    </div>
  );
};
